import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  EcheanceSens,
  PlanPaiementFrequence,
  PlanPaiementRow,
  PlanPaiementStatut,
} from '@/lib/database.types';

export function usePlansPaiement(filter: {
  entrepriseId?: string | null;
  tierId?: string | null;
  recouvrementId?: string | null;
  statut?: PlanPaiementStatut | null;
  sens?: EcheanceSens | null;
}) {
  return useQuery({
    queryKey: ['plans_paiement', filter],
    queryFn: async (): Promise<PlanPaiementRow[]> => {
      let q = supabase.from('plans_paiement').select('*').order('created_at', { ascending: false });
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.recouvrementId) q = q.eq('recouvrement_id', filter.recouvrementId);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.sens) q = q.eq('sens', filter.sens);
      q = q.limit(500);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export interface CreatePlanPaiementInput {
  entreprise_id: string;
  tier_id: string;
  sens: EcheanceSens;
  activite_id?: string | null;
  facture_ref?: string | null;
  motif?: string | null;
  devise_code?: string;
  taux_change?: number;
  montant_total: number;
  nb_echeances: number;
  date_premiere: string;
  frequence?: PlanPaiementFrequence;
  intervalle_jours?: number | null;
  observation?: string | null;
  recouvrement_id?: string | null;
}

export function useCreatePlanPaiement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePlanPaiementInput): Promise<PlanPaiementRow> => {
      const { data, error } = await supabase
        .from('plans_paiement')
        .insert(input)
        .select('*')
        .single();
      if (error) throw error;
      // Génération auto des tranches
      const { error: rpcError } = await supabase.rpc('plans_paiement_generer_echeances', {
        p_plan_id: data.id,
      });
      if (rpcError) throw rpcError;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plans_paiement'] });
      void qc.invalidateQueries({ queryKey: ['echeances'] });
    },
  });
}

export function useDeletePlanPaiement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('plans_paiement').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plans_paiement'] });
      void qc.invalidateQueries({ queryKey: ['echeances'] });
    },
  });
}

export function useRecomputePlanStatut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planId: string): Promise<PlanPaiementStatut> => {
      const { data, error } = await supabase.rpc('plans_paiement_recompute_statut', {
        p_plan_id: planId,
      });
      if (error) throw error;
      return data as unknown as PlanPaiementStatut;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['plans_paiement'] });
    },
  });
}
